import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { PawPrint, Search, User, BookOpen } from "lucide-react";
import { useAuth } from "../lib/auth";

const Nav = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "G")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header
      data-testid="main-nav"
      className="sticky top-0 z-40 backdrop-blur-md bg-brand-cream/80 border-b border-brand-border-warm"
    >
      <div className="max-w-7xl mx-auto px-3 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-2 group shrink-0"
        >
          <span className="w-9 h-9 rounded-full bg-brand-forest flex items-center justify-center text-white shadow-warm group-hover:bg-brand-forest-dark transition-colors">
            <PawPrint className="w-5 h-5" strokeWidth={2.4} />
          </span>
          <span className="font-heading text-xl sm:text-2xl font-black text-brand-ink tracking-tight">
            Pawground
          </span>
        </Link>

        <nav className="flex items-center gap-1 sm:gap-4">
          {/* Primary navigation. On mobile (<640px) we show icon-only
              pills with larger tap targets so all three links fit
              alongside the auth CTAs without a hamburger. On sm+ we
              expand to full labels with smaller padding for density. */}
          <Link
            to="/search"
            data-testid="nav-search"
            aria-label="Explore"
            className="inline-flex items-center justify-center sm:justify-start gap-2 text-sm font-medium text-brand-ink/80 hover:text-brand-forest transition-colors w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-2 rounded-full hover:bg-brand-forest/8 sm:hover:bg-transparent"
          >
            <Search className="w-5 h-5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Explore</span>
          </Link>
          <Link
            to="/private-parks"
            data-testid="nav-private-parks"
            aria-label="Private Parks"
            title="Private Parks"
            className="inline-flex items-center justify-center sm:justify-start gap-1.5 text-sm font-medium text-brand-ink/80 hover:text-brand-forest transition-colors w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-2 rounded-full hover:bg-brand-forest/8 sm:hover:bg-transparent"
          >
            <span className="text-lg sm:text-base leading-none" aria-hidden>🏡</span>
            <span className="hidden sm:inline">Private Parks</span>
          </Link>
          <Link
            to="/guides"
            data-testid="nav-guides"
            aria-label="Guides"
            className="inline-flex items-center justify-center sm:justify-start gap-2 text-sm font-medium text-brand-ink/80 hover:text-brand-forest transition-colors w-10 h-10 sm:w-auto sm:h-auto sm:px-3 sm:py-2 rounded-full hover:bg-brand-forest/8 sm:hover:bg-transparent"
          >
            <BookOpen className="w-5 h-5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Guides</span>
          </Link>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/profile"
                data-testid="nav-profile"
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-brand-border-warm bg-white hover:border-brand-forest transition-colors"
              >
                <span className="w-7 h-7 rounded-full bg-brand-forest text-white text-xs font-bold flex items-center justify-center">
                  {initials}
                </span>
                <span className="text-sm font-medium text-brand-ink hidden sm:inline">
                  {user.name.split(" ")[0]}
                </span>
              </Link>
              <button
                data-testid="nav-logout"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-xs text-brand-ink/60 hover:text-brand-forest px-2"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2 ml-1 sm:ml-0">
              <Link
                to="/login"
                data-testid="nav-login"
                // rel="nofollow" so Googlebot doesn't crawl into the
                // login flow and discover hundreds of
                // /login?redirect=... variants (see the
                // SignupPromptModal CTA which IS the param-variant
                // source). robots.txt also Disallows /login as a
                // second line of defense. All other /login links
                // across the app carry the same rel.
                rel="nofollow"
                className="hidden sm:inline-flex text-sm font-medium text-brand-ink/80 hover:text-brand-forest px-3 py-2"
              >
                Log in
              </Link>
              <Link
                to="/signup"
                data-testid="nav-signup"
                className="bg-brand-forest text-white text-sm font-semibold rounded-full px-3 sm:px-4 py-2 hover:bg-brand-forest-dark transition-colors active:scale-95 shadow-warm flex items-center gap-1.5 whitespace-nowrap shrink-0"
              >
                <User className="w-4 h-4" />
                Sign up
              </Link>
            </div>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Nav;
